import { NPCS, NPC_IDS, type DialogueLine, type NpcDefinition, type NpcId, postingAt } from '@/data/npcs';
import { type Festival, festivalPosting } from '@/data/festivals';
import type { Season } from '@/time/clock';
import type { Weather } from '@/time/weather';

/**
 * Where everyone is, and what they have to say about it.
 *
 * Derived from the clock rather than simulated: an NPC's position is a
 * function of the hour, not something that accumulates. That is the same rule
 * the weather follows, and it means nobody can drift, get stuck on a fence, or
 * need saving.
 *
 * The cost is that they teleport between postings rather than walking. Worth
 * paying for now — a villager who walks needs pathfinding, and pathfinding is
 * a bigger thing than the first villager deserves.
 */

export interface NpcPresence {
  readonly id: NpcId;
  readonly definition: NpcDefinition;
  /** Tile coordinates. */
  readonly x: number;
  readonly y: number;
}

export interface DialogueContext {
  readonly hour: number;
  readonly season: Season;
  readonly weather: Weather;
  /** Today's festival, if today is one. */
  readonly festival?: string | null;
}

export class NpcCrew {
  /** Which line each person is up to, so repeated talks are not identical. */
  private readonly cursor = new Map<NpcId, number>();

  /**
   * Everyone standing in an area at this hour.
   *
   * A festival replaces the day's postings for whoever is invited, so the day
   * does not need its own code path: attendance is a posting like any other,
   * and someone not invited keeps their ordinary schedule.
   */
  in(areaId: string, hour: number, festival: Festival | null = null): NpcPresence[] {
    const present: NpcPresence[] = [];

    for (const id of NPC_IDS) {
      const definition = NPCS[id];
      const posting =
        (festival && festivalPosting(festival, id, hour)) ?? postingAt(definition, hour);
      if (!posting || posting.area !== areaId) continue;

      present.push({ id, definition, x: posting.x ?? 0, y: posting.y ?? 0 });
    }

    return present;
  }

  /** Whoever is standing on a tile, if anyone. */
  at(
    areaId: string,
    hour: number,
    tileX: number,
    tileY: number,
    festival: Festival | null = null,
  ): NpcPresence | null {
    return (
      this.in(areaId, hour, festival).find((npc) => npc.x === tileX && npc.y === tileY) ?? null
    );
  }

  isAbout(id: NpcId, areaId: string, hour: number, festival: Festival | null = null): boolean {
    return this.in(areaId, hour, festival).some((npc) => npc.id === id);
  }

  /**
   * What they say now.
   *
   * The first line whose conditions all hold, so specific lines are written
   * above general ones and the last line acts as the fallback. When several
   * general lines match, talking again moves along them — a villager who
   * repeats one sentence is furniture that happens to have a name.
   */
  speak(npc: NpcDefinition, context: DialogueContext): string {
    const matching = npc.lines.filter((line) => matches(line, context));
    if (matching.length === 0) return '…';

    // A specific line always wins. Only the general ones rotate.
    const specific = matching.find((line) => line.when !== undefined);
    if (specific) return specific.text;

    const index = this.cursor.get(npc.id) ?? 0;
    this.cursor.set(npc.id, index + 1);
    return matching[index % matching.length]!.text;
  }
}

function matches(line: DialogueLine, context: DialogueContext): boolean {
  const when = line.when;
  if (!when) return true;

  if (when.hourFrom !== undefined && context.hour < when.hourFrom) return false;
  if (when.hourTo !== undefined && context.hour >= when.hourTo) return false;
  if (when.season !== undefined && context.season !== when.season) return false;
  if (when.festival !== undefined && context.festival !== when.festival) return false;
  if (when.raining !== undefined) {
    const raining = context.weather === 'rain' || context.weather === 'storm';
    if (raining !== when.raining) return false;
  }

  return true;
}
